<template>
  <div class="modal">
    <div class="modal-header__container">
      <div class="modal__header">
        <div class="modal__action inline-flex spacex-4">
          <div v-if="get(context, 'tender.title')" class="tender-ellipsis-name">
            {{ context.tender.title }}
          </div>
          <div v-if="get(context, 'tender.list')">– {{ context.tender.list }}</div>
        </div>
        <h2 class="modal__title" :title="context.row.description_of_works">
          {{ context.row.description_of_works }}
        </h2>
        <button class="modal__close-button" @click="handleClose">
          <CloseIcon class="close-button__icon" />
        </button>
      </div>
      <div class="hr" />
    </div>
    <div style="display: contents">
      <div
        class="modal-content__container"
        :class="{
          'modal-content__small-container': !isMainTab || isIncomingTender,
        }"
      >
        <Tabs
          v-if="!isIncomingTender"
          v-model="currentStep"
          :tabs="tabs"
          @change="updateComments"
        />
        <div v-if="isMainTab" class="position-info text-sm">
          <div v-for="(field, idx) in positionFields" :key="idx" class="d-contents">
            <div v-if="getField(field)" class="d-contents">
              <span class="grey-600 position-info_key">{{ field.title }}</span>
              <span class="grey-900 position-info_value">{{ getField(field) }}</span>
            </div>
          </div>
        </div>
        <div class="comments-header">
          <div class="h5 grey-900">
            {{ $t('Last comments') }}
          </div>
          <div class="text-sm grey-400">
            <InfoIcon class="icon-g-400 icon-13px" />
            {{ $t('Visible only to') }} {{ currentCompany }} {{ $t('and you') }}
          </div>
        </div>
        <div id="messages" class="comments-messages-container spacey-16">
          <Message
            v-for="comment in comments"
            :key="comment.id"
            always-not-mine
            :user-id="comment.id"
            :message="comment"
          />
          <Info v-if="!comments.length" class="comments-info" :title="$t('No messages yet')">
            {{ $t('You can leave a comment here and Fixtendeer construction will answer') }}. <br />
            {{ $t('All comments will also be displayed in the general chat in the summary tab') }}.
          </Info>
        </div>
      </div>
      <div class="modal-navigation__container">
        <MessageInput :placeholder="$t('Write a comment...')" @send="sendComment" />
      </div>
    </div>
  </div>
</template>

<script>
  import CloseIcon from '@/assets/icons/x.svg';
  import Tabs from '@/containers/tenders/create/Tabs';
  import { mapActions, mapState } from 'vuex';
  import factory from '@/utils/api/factory';
  import get from 'lodash.get';
  import InfoIcon from '@/assets/icons/error-red.svg';
  import MessageInput from '@/components/chat/MessageInput';
  import Message from '@/components/chat/Message';
  import Info from '@/components/chat/Info';
  import request from '@/utils/api/request';
  import commentsSubscription from '@/gql/estimate/commentsSubscription.graphql';
  import { changeQuery, deleteQuery } from '@/utils/router';

  const MIN_SCROLL_HEIGHT = 100;

  export default {
    name: 'TenderPositionCard',
    components: {
      CloseIcon,
      Tabs,
      InfoIcon,
      MessageInput,
      Message,
      Info,
    },
    apollo: {
      $subscribe: {
        generalCommentsSubscribe() {
          return {
            query: commentsSubscription,
            variables: {
              id: this.context?.rowId,
            },
            result({ data }) {
              this.generalComments = get(data, 'comments', []);
            },
          };
        },
      },
    },
    data() {
      return {
        currentStep: 0,
        offerComments: [],
        generalComments: [],
        nextUrl: null,
        interval: null,
        positionFields: [
          {
            slug: 'description_of_works',
            title: 'Description',
          },
          {
            slug: 'unit.name',
            title: 'Unit',
          },
          {
            slug: 'quantity',
            title: 'Quantity',
          },
        ],
      };
    },
    async created() {
      if (
        this.$route.query.tab &&
        !this.isIncomingTender &&
        this.tabs.some((tab) => tab.id === +this.$route.query.tab)
      ) {
        this.currentStep = +this.$route.query.tab;
      } else {
        this.currentStep = this.context.rowId;
      }
      await this.updateComments(this.currentStep);
    },
    mounted() {
      let el = this.$el.querySelector('#messages');
      this.interval = setInterval(() => {
        if (this.nextUrl && get(el, 'scrollTop', MIN_SCROLL_HEIGHT) < MIN_SCROLL_HEIGHT) {
          let heightBefore = el.scrollHeight;
          this.uploadMoreComments().then(() => {
            el.scrollTop = el.scrollHeight - heightBefore;
          });
        }
      }, 1000);
    },
    updated() {
      this.scrollToEnd();
    },
    computed: {
      ...mapState('modal', ['context']),
      isGeneralComments() {
        return this.currentStep === this.context.rowId && !this.isIncomingTender;
      },
      isIncomingTender() {
        return get(this.context, 'isIncomingTender', false);
      },
      comments() {
        return this.isGeneralComments ? this.generalComments : this.offerComments;
      },
      offers() {
        return get(this.context, 'offers', false);
      },
      currentCompany() {
        if (this.isIncomingTender) {
          return get(this.context, 'tenderTeam', this.$t('general contractor'));
        } else {
          let currentTab = this.tabs.find((tab) => tab.id === this.currentStep);
          return currentTab?.main ? this.$t('your teammates') : currentTab.title;
        }
      },
      isMainTab() {
        return this.currentStep === this.context.rowId;
      },
      tabs() {
        const DEFAULT_TAB = {
          id: this.context.rowId,
          title: 'General info',
          main: true,
        };

        let order = 0;

        return this.offers
          ? [
              DEFAULT_TAB,
              ...this.offers.map((offer) => {
                return {
                  order: order++,
                  count: 0,
                  id: offer.row_id,
                  title: offer.team,
                };
              }),
            ]
          : [DEFAULT_TAB];
      },
    },
    methods: {
      get,
      ...mapActions({
        close: 'modal/closeModal',
      }),
      handleClose() {
        deleteQuery(['position', 'tab']);
        this.close();
      },
      getField(field) {
        return get(this.context.row, field.slug);
      },
      async updateComments(id) {
        if (!this.isIncomingTender) {
          await changeQuery({
            tab: id,
          });
        }
        if (!this.isGeneralComments) {
          let { data } = await factory('tender_comments').index({
            tender_offer_row_id: id,
            order: 'created_at',
          });

          this.offerComments = data.results;
          this.nextUrl = data.next;
        }
      },
      async uploadMoreComments() {
        if (!this.isGeneralComments) {
          let { data } = await request(this.nextUrl);

          this.offerComments = [...data.results, ...this.offerComments];
          this.nextUrl = data.next;
        }
      },
      async sendComment(text) {
        if (this.isGeneralComments) {
          await factory('rows').postInResourcePath(this.context.rowId, 'comments', {
            text,
          });
        } else {
          let { data } = await factory('tender_comments').customPost({
            tender_offer_row_id: this.currentStep,
            text,
          });

          this.offerComments.push(data);
          this.scrollToEnd();
        }
      },
      scrollToEnd() {
        let container = this.$el.querySelector('#messages');
        if (container) container.scrollTop = container.scrollHeight;
      },
      beforeDestroy() {
        clearInterval(this.interval);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/styles/colors';

  .modal {
    height: 660px;
  }

  .message__form {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px 12px 12px;

    background: white;
    box-shadow: inset 0 1px 0 $grey100, inset 1px 0 0 #dadde2;
  }

  .message-and-files {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    overflow: hidden;
  }

  .message__input {
    margin-left: 12px;
    color: $grey900;
    font-weight: normal;
    font-size: 14px;
  }

  .messages-container {
    padding: 20px;

    overflow: auto;
    max-height: calc(100vh - 200px);
    position: absolute;
    top: 48px;
    width: 100%;
  }

  .message-date {
    text-align: center;
    color: $grey700;
    font-size: 14px;
    padding: 4px 8px;
  }

  .message__attach-files {
    display: flex;
    cursor: pointer;

    &:hover {
      .attach-files__icon path {
        fill: $grey600;
      }
    }
  }

  .comments-header {
    background: $grey50;
    padding: 16px 20px;
    box-shadow: inset 0 1px 0 #e8ebf0, inset 1px 0 0 #e8ebf0;

    display: flex;
    align-items: center;
    justify-content: space-between;

    .text-sm {
      display: flex;
      align-items: center;
    }
  }

  .comments-messages-container {
    padding: 0 20px 20px 20px;
    overflow: auto;
    background: $grey50;
  }

  .modal-content__container {
    overflow: hidden;
    bottom: 45px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows:
      minmax(min-content, max-content) minmax(min-content, max-content) minmax(
        min-content,
        max-content
      )
      1fr;
  }

  .modal-content__small-container {
    grid-template-rows:
      minmax(min-content, max-content) minmax(min-content, max-content)
      1fr;
  }

  .position-info {
    display: grid;
    grid-template-columns: minmax(auto, auto) 1fr;
    grid-gap: 8px;
    width: fit-content;

    padding: 30px 20px;
  }

  .comments-info {
    max-width: 50%;
  }

  .modal__title {
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    white-space: nowrap;
  }
</style>
